import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

/**
 * F17 lifecycle state — 5 values that mirror `BookingStatus` from
 * `@bravemove/types/booking`. Declared locally here (and not imported from
 * `@bravemove/types`) to keep `@bravemove/ui-web` free of a cross-package dep —
 * mirrors the `BookingStatusRibbon` precedent in this package. The string
 * literal union is structurally identical, so values from
 * `@bravemove/types`'s `BookingStatus` are assignable to it.
 *
 * F02's `BookingStatusRibbon` covers the larger inline pill; this is the
 * smaller monospace badge with letter-spacing used in row / cell / tooltip
 * contexts (BookingCard rows, calendar block tooltips, admin Tables).
 *
 * Spec source: `docs/design/screens/booking-lib.jsx` — `lifecycleCfg` +
 * `LifecycleChip`. F16-F18 design polish wave, Wave 1 1.1.
 */
export type LifecycleState = 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';

export type LifecycleChipSize = 'sm' | 'md';

export interface LifecycleChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  readonly state: LifecycleState;
  readonly size?: LifecycleChipSize;
  /** Optional label override. Defaults to the canonical label per state. */
  readonly label?: string;
}

/** Semantic tone resolved to Tailwind utilities via tokens. */
type Tone = 'warning' | 'info' | 'success' | 'danger';

const STATE_TONE: Record<LifecycleState, Tone> = {
  pending: 'warning',
  confirmed: 'info',
  completed: 'success',
  cancelled: 'danger',
  no_show: 'danger',
};

const STATE_LABEL: Record<LifecycleState, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No-show',
};

const TONE_CLASSES: Record<Tone, { bg: string; text: string; dot: string; border: string }> = {
  warning: {
    bg: 'bg-warning/15',
    text: 'text-ink',
    dot: 'bg-warning',
    border: 'border-warning/40',
  },
  info: { bg: 'bg-info/15', text: 'text-ink', dot: 'bg-info', border: 'border-info/40' },
  success: {
    bg: 'bg-success/15',
    text: 'text-ink',
    dot: 'bg-success',
    border: 'border-success/40',
  },
  danger: {
    bg: 'bg-danger/15',
    text: 'text-ink',
    dot: 'bg-danger',
    border: 'border-danger/40',
  },
};

// Spec sizes from booking-lib.jsx:
//   sm: padding 2px 7px, font 9px, dot 5px
//   md: padding 4px 9px, font 10px, dot 6px
const SIZE_CLASSES: Record<LifecycleChipSize, { box: string; dot: string; text: string }> = {
  sm: { box: 'gap-1.5 px-[7px] py-[2px]', dot: 'size-[5px]', text: 'text-[9px]' },
  md: { box: 'gap-1.5 px-[9px] py-[4px]', dot: 'size-[6px]', text: 'text-[10px]' },
};

/**
 * Small monospace badge surfacing booking lifecycle state. Distinct from
 * `BookingStatusRibbon` (the larger inline pill); use this in row / tooltip /
 * cell contexts where the spec calls for an uppercase mono badge with letter
 * spacing.
 */
export function LifecycleChip({
  state,
  size = 'md',
  label,
  className,
  ...rest
}: LifecycleChipProps) {
  const tone = TONE_CLASSES[STATE_TONE[state]];
  const sz = SIZE_CLASSES[size];
  const text = label ?? STATE_LABEL[state];
  return (
    <span
      role="status"
      aria-label={text}
      data-state={state}
      className={cn(
        'inline-flex items-center rounded-[4px] border font-mono font-bold uppercase whitespace-nowrap align-middle tracking-[0.1em]',
        sz.box,
        sz.text,
        tone.bg,
        tone.text,
        tone.border,
        className,
      )}
      {...rest}
    >
      <span aria-hidden="true" className={cn('rounded-full shrink-0', sz.dot, tone.dot)} />
      <span>{text}</span>
    </span>
  );
}

export default LifecycleChip;
